// Sugalarm — the single canonical Settings section-card.
//
// Cycle 74 introduced the subsection-card primitive; Cycle 129 added the
// colored-header treatment (a tinted icon tile + per-section accent + a
// one-line description). Cycle 130 consolidated the two — the colored-
// header implementation that briefly lived in a parallel `SettingsSection`
// component now lives HERE, and every Settings pane renders through this
// one component. No duplicate remains.
//
// Each section reads as a self-contained, scannable card with a COLORED
// header. Colors come exclusively from the central section-accent tokens
// (`--sec-primary | -amber | -violet | -cyan | -steel | -rose | -green`,
// defined in `theme/tokens.css`). The chosen accent is published as a
// local `--sec` custom property on the card; the header tint and icon
// tile are `color-mix` of that accent over the card surface background
// fill token (never element `opacity`), and the title / description stay
// fully opaque. No hardcoded hex here.
//
// Two ways to pick the accent:
//   - `accent` — an explicit section-accent token name (the Cycle 129
//     per-section assignments use this).
//   - `tone` — the legacy cycle-74 vocabulary, mapped to an accent
//     (neutral→steel, accent→primary, warn→amber, urgent→rose, ok→green).
// `accent` wins when both are given.
//
// Collapsible support (used by Test alarms) is controlled via `open` /
// `onToggle`; when those are omitted the card self-manages from
// `defaultOpen`. A header-only card (no `children`) bleeds the header to
// fill the card — used by the Alerts master toggle.

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import styles from "./SubsectionCard.module.css";
import { lookupCardTheme } from "./settingsCardTheme";

/* Cycle 165: opt-in frosted-glass surface for the mobile Settings panes.
   The card stays solid `--bg-elev` by DEFAULT — every kiosk/web call
   site renders byte-identical. Glass is opted into either per-card via
   the `glass` prop, or for a whole subtree via this context (the mobile
   Settings drill-down mounts the provider with `variant === "mobile"`,
   so all section cards go glass on mobile and only on mobile). An
   explicit `glass` prop wins over the context. */
export const SettingsCardGlassContext = createContext(false);

/* Cycle 172: collapsible Settings detail-pane cards. A non-null value (the
   active section id) turns EVERY uncontrolled SubsectionCard in the
   subtree collapsible (default expanded) and persists each card's open/
   closed state device-local under
   `sugalarm.settingsCollapse.<sectionId>.<title-slug>`. Mounted around the
   mobile + web drill-down detail panes only — the kiosk pane leaves the
   context null so its fixed 1024×600 layout is unchanged (headers stay
   non-interactive). An explicit `collapsible` + `open`/`onToggle` card
   (e.g. Test alarms) keeps its controlled behavior regardless. */
export const SettingsCollapseContext = createContext<string | null>(null);

/* Cycle 173: kiosk-only-card gating. The value is the caller's live
   paired-kiosk state derived from GET /api/devices — `true` (a live kiosk
   token exists), `false` (none), or `null` (devices not loaded yet →
   treated as locked, fail-safe). A `kioskOnly` card renders LOCKED
   (collapsed, header non-interactive, "For kiosk users only") whenever
   this is not `true`; when it IS `true` the card is byte-identical to a
   normal (step-2) card. UX only — the server stays authoritative. */
export const SettingsKioskContext = createContext<boolean | null>(null);

/* Cycle 207: Android-tablet-kiosk-only card theming. The value is the
   active section id while the card is rendered inside the Android-tablet
   Settings pane (kiosk variant AND `isAndroidTabletClassRuntime()`), else
   null. When set, a card that resolves a `settingsCardTheme` registry
   entry renders the registry EMOJI in place of its glyph icon and takes the
   registry per-card ACCENT — so the tablet panes read as colorful,
   scannable stacks. Mounted ONLY around the tablet kiosk pane; the Pi
   kiosk, iPad kiosk, web, and mobile leave it null and render the existing
   glyph + accent byte-identically. The shared card never does its own
   device detection — the tablet signal arrives here via context. */
export const SettingsTabletThemeContext = createContext<string | null>(null);

/* Cycle 207: Expand all / Collapse all controller. The tablet Settings
   pane mounts a controller so a single header button can drive every
   collapsible card through the SAME per-card state + persistence as an
   individual header tap (no second collapse mechanism). Each uncontrolled,
   context-collapsible card registers a `setOpen`/`isOpen` pair on mount and
   notifies the controller when the user toggles it so the button label can
   reflect "any card open". Null on every non-tablet surface. */
export interface CollapseAllRegistration {
  setOpen: (open: boolean) => void;
  isOpen: () => boolean;
}
export interface CollapseAllController {
  register: (reg: CollapseAllRegistration) => () => void;
  notifyChange: () => void;
}
export const SettingsCollapseAllContext =
  createContext<CollapseAllController | null>(null);

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function readPersistedOpen(key: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    if (v === "0") return false;
    if (v === "1") return true;
  } catch {
    /* private mode — fall through to the default */
  }
  return fallback;
}

export type SectionAccent =
  | "primary"
  | "amber"
  | "violet"
  | "cyan"
  | "steel"
  | "rose"
  | "green";

export type SubsectionTone = "neutral" | "accent" | "warn" | "urgent" | "ok";

const ACCENT_VAR: Record<SectionAccent, string> = {
  primary: "var(--sec-primary)",
  amber: "var(--sec-amber)",
  violet: "var(--sec-violet)",
  cyan: "var(--sec-cyan)",
  steel: "var(--sec-steel)",
  rose: "var(--sec-rose)",
  green: "var(--sec-green)",
};

const TONE_TO_ACCENT: Record<SubsectionTone, SectionAccent> = {
  neutral: "steel",
  accent: "primary",
  warn: "amber",
  urgent: "rose",
  ok: "green",
};

export interface SubsectionCardProps {
  title: string;
  /** One-line description under the title. `description` and the legacy
   *  `subtitle` are aliases; `description` wins when both are set. */
  description?: string;
  subtitle?: string;
  icon?: ReactNode;
  /** Explicit section accent (wins over `tone`). */
  accent?: SectionAccent;
  /** Legacy cycle-74 tone vocabulary, mapped to an accent. */
  tone?: SubsectionTone;
  /** Optional right-aligned header slot: status pill, summary, or a
   *  master toggle. */
  headerRight?: ReactNode;
  collapsible?: boolean;
  open?: boolean;
  onToggle?: () => void;
  defaultOpen?: boolean;
  /** Cycle 172: when collapsible (explicitly or via context), the initial
   *  state if nothing is persisted yet. Default false → expanded, so no
   *  card is ever hidden on first visit. */
  defaultCollapsed?: boolean;
  /** Cycle 173: this card's controls are only useful on a paired kiosk.
   *  Locked (collapsed + non-interactive + "For kiosk users only") unless
   *  SettingsKioskContext is `true`; byte-identical to a normal card when
   *  a kiosk is paired. */
  kioskOnly?: boolean;
  /** Cycle 165: frosted-glass surface (mobile Settings). Default false →
   *  the solid `--bg-elev` card, byte-identical for kiosk/web. Omitted →
   *  inherits `SettingsCardGlassContext`. The tinted colored header +
   *  icon tile render unchanged over the glass body. */
  glass?: boolean;
  children?: ReactNode;
}

// Cycle 173: the lock affordance on a kiosk-only card without a paired
// kiosk (muted padlock, inherits the locked header ink — no new hex).
const LockGlyph = () => (
  <svg
    className={styles.lockGlyph}
    width="14"
    height="14"
    viewBox="0 0 14 14"
    aria-hidden="true"
  >
    <rect x="3" y="6.2" width="8" height="5.5" rx="1.2" fill="currentColor" />
    <path
      d="M4.6 6.2 V4.7 a2.4 2.4 0 0 1 4.8 0 V6.2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
    />
  </svg>
);

const Chevron = ({ open }: { open: boolean }) => (
  <svg
    className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`}
    width="14"
    height="14"
    viewBox="0 0 14 14"
    aria-hidden="true"
  >
    <path
      d="M5 3 L9 7 L5 11"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export function SubsectionCard({
  title,
  description,
  subtitle,
  icon,
  accent,
  tone = "neutral",
  headerRight,
  collapsible = false,
  open,
  onToggle,
  defaultOpen = true,
  defaultCollapsed = false,
  kioskOnly = false,
  glass,
  children,
}: SubsectionCardProps) {
  const ctxGlass = useContext(SettingsCardGlassContext);
  const isGlass = glass ?? ctxGlass;
  // Cycle 308: the mobile-Settings redesign surface — the ~46% translucent
  // panel with the iridescent border + the solid saturated header band.
  // Applied ONLY when glass arrives via the SETTINGS context (the mobile
  // Settings drill-down provider), never via the explicit `glass` prop, so
  // the Alerts-history + Household mobile cards (which opt in per-card)
  // keep the cycle-165 glass byte-identical.
  const settingsSurface = glass === undefined && ctxGlass;

  // Cycle 173: locked when a kiosk-only card has no live paired kiosk
  // (false) or the device list isn't loaded yet (null → fail-safe locked).
  const kioskState = useContext(SettingsKioskContext);
  const locked = kioskOnly && kioskState !== true;

  // Cycle 172: the collapse context turns uncontrolled cards collapsible
  // and gives them a stable device-local persistence key. A card that is
  // explicitly controlled (`open`/`onToggle`, e.g. Test alarms) is left
  // alone by the context.
  const collapseSection = useContext(SettingsCollapseContext);
  const controlled = open !== undefined;
  const contextCollapsible = collapseSection != null && !controlled;
  // A locked card always shows the collapsed/locked affordance.
  const isCollapsible = collapsible || contextCollapsible || locked;
  const persistKey = contextCollapsible
    ? `sugalarm.settingsCollapse.${collapseSection}.${slugify(title)}`
    : null;

  const [selfOpen, setSelfOpen] = useState(() =>
    persistKey
      ? readPersistedOpen(persistKey, !defaultCollapsed)
      : defaultCollapsed
        ? false
        : defaultOpen,
  );
  // Locked cards are force-collapsed and cannot be expanded.
  const isOpen = locked ? false : controlled ? (open as boolean) : selfOpen;

  // Cycle 207: persist + apply a new open-state for an uncontrolled card.
  // Shared by the user header tap AND the Expand all / Collapse all driver
  // so both write through the same selfOpen + persistKey path.
  const persistOpen = (next: boolean) => {
    if (persistKey) {
      try {
        localStorage.setItem(persistKey, next ? "1" : "0");
      } catch {
        /* private mode — state still toggles for this session */
      }
    }
  };

  // Cycle 207: register with the Expand all / Collapse all controller (the
  // tablet pane mounts one). Only uncontrolled, context-collapsible,
  // unlocked cards participate — controlled cards (Test alarms) keep their
  // own open/onToggle behavior, and a locked card is never expandable.
  const collapseAll = useContext(SettingsCollapseAllContext);
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  const participatesInAll =
    collapseAll != null && contextCollapsible && !controlled && !locked;
  useEffect(() => {
    if (!collapseAll || !participatesInAll) return;
    return collapseAll.register({
      isOpen: () => isOpenRef.current,
      setOpen: (next) => {
        setSelfOpen(next);
        persistOpen(next);
      },
    });
    // persistKey is stable for the card's lifetime; setSelfOpen is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapseAll, participatesInAll]);

  const toggle = () => {
    if (onToggle) {
      onToggle();
      return;
    }
    setSelfOpen((o) => {
      const next = !o;
      persistOpen(next);
      return next;
    });
    // Let the controller recompute its "any card open" label.
    collapseAll?.notifyChange();
  };

  // Cycle 207: on the Android tablet kiosk, swap the glyph for the registry
  // emoji + take the registry accent. Resolves to null on every other
  // surface (context is null) or for a card with no registry entry, so the
  // existing icon + accent render unchanged.
  const tabletThemeSection = useContext(SettingsTabletThemeContext);
  const tabletTheme = tabletThemeSection
    ? lookupCardTheme(tabletThemeSection, title)
    : null;
  const resolvedAccent = tabletTheme?.accent ?? accent ?? TONE_TO_ACCENT[tone];
  const desc = description ?? subtitle;
  // A header-only card (e.g. the Alerts master toggle) has no body — the
  // header bleeds to fill the whole card so there's no empty gap.
  const hasBody = children != null && children !== false;

  // Cycle 207: the tablet renders the registry emoji in the tile; every
  // other surface renders the existing glyph. The tile shows whenever
  // there's something to put in it.
  const tileContent = tabletTheme ? (
    <span className={styles.iconEmoji}>{tabletTheme.emoji}</span>
  ) : (
    icon
  );
  const headerInner = (
    <>
      {tileContent ? (
        <span className={styles.iconTile} aria-hidden="true">
          {tileContent}
        </span>
      ) : null}
      <div className={styles.headerText}>
        <span className={styles.headerTitle}>{title}</span>
        {locked ? (
          <span className={styles.lockedNote}>For kiosk users only</span>
        ) : desc ? (
          <span className={styles.headerDescription}>{desc}</span>
        ) : null}
      </div>
      {headerRight && !locked ? (
        <div className={styles.headerRight}>{headerRight}</div>
      ) : null}
      {locked ? (
        <LockGlyph />
      ) : isCollapsible ? (
        <Chevron open={isOpen} />
      ) : null}
    </>
  );

  return (
    <section
      className={`${styles.card} ${isGlass ? styles.cardGlass : ""} ${
        settingsSurface ? styles.cardSettings : ""
      } ${locked ? styles.cardLocked : ""}`}
      style={{ ["--sec" as string]: ACCENT_VAR[resolvedAccent] }}
    >
      {locked ? (
        // Cycle 173: header non-interactive (disabled button) so the card
        // can't be expanded to reveal controls a phone-only user can't use.
        <button
          type="button"
          className={`${styles.header} ${hasBody ? "" : styles.headerOnly}`}
          disabled
          aria-disabled="true"
        >
          {headerInner}
        </button>
      ) : isCollapsible ? (
        <button
          type="button"
          className={`${styles.header} ${hasBody ? "" : styles.headerOnly}`}
          onClick={toggle}
          aria-expanded={isOpen}
        >
          {headerInner}
        </button>
      ) : (
        <div className={`${styles.header} ${hasBody ? "" : styles.headerOnly}`}>
          {headerInner}
        </div>
      )}
      {/* Cycle 172: collapsible cards keep the body mounted and animate
          height (grid-rows 0fr↔1fr; instant under prefers-reduced-motion)
          so form state survives a collapse and content stays opaque.
          Non-collapsible cards render exactly as before (byte-identical
          for kiosk + every legacy call site). */}
      {/* Cycle 173: a locked card renders header-only — the body (its
          kiosk controls) is not in the DOM at all, so nothing is
          focusable or reachable until a kiosk is paired. */}
      {hasBody && !locked ? (
        isCollapsible ? (
          <div className={styles.bodyWrap} data-open={isOpen ? "1" : "0"}>
            <div className={styles.body} aria-hidden={!isOpen}>
              {children}
            </div>
          </div>
        ) : (
          <div className={styles.body}>{children}</div>
        )
      ) : null}
    </section>
  );
}
